/**
 * Fuente unica de los items de navegacion del sidebar (menos "Mi unidad", que depende de
 * `authz.units` y no de un permiso fijo). La usan AppShell (menu) y CommandPalette (buscador
 * Ctrl/Cmd+K) para no mantener dos listas de "quien ve que" que puedan desalinearse.
 */
import { can, type MyAuthz } from '@rat/shared';
import { hasAnywhere, type AccessResponse } from '../auth/access';

export type NavGroup = 'tableros' | 'operacion' | 'institucional' | 'administracion';

export const GROUP_LABEL: Record<NavGroup, string> = {
  tableros: 'Tableros',
  operacion: 'Operación',
  institucional: 'Institucional',
  administracion: 'Administración',
};

export const GROUP_ORDER: NavGroup[] = ['tableros', 'operacion', 'institucional', 'administracion'];

export interface NavItem {
  key: string;
  label: string;
  to: string;
  end?: boolean;
  group: NavGroup;
  show: (ctx: { access: AccessResponse; authz: MyAuthz }) => boolean;
}

/** Mismo orden y mismas condiciones que tenia AppShell antes de agrupar -- ver el commit que agrego esto. */
export const NAV_ITEMS: NavItem[] = [
  { key: 'tab-admin', label: 'Tablero del administrador', to: '/tablero-admin', group: 'tableros', show: ({ access }) => hasAnywhere(access, 'dashboard.admin') },
  { key: 'tab-auditor', label: 'Tablero del auditor', to: '/auditor', group: 'tableros', show: ({ access }) => hasAnywhere(access, 'dashboard.auditor') },
  { key: 'tab-jefe', label: 'Tablero del jefe', to: '/jefe', group: 'tableros', show: ({ access }) => hasAnywhere(access, 'dashboard.jefe') },
  { key: 'tab-revisor', label: 'Tablero del revisor', to: '/revision', end: true, group: 'tableros', show: ({ access }) => hasAnywhere(access, 'dashboard.revisor') },
  { key: 'bandeja', label: 'Bandeja de revisión', to: '/revision/bandeja', group: 'operacion', show: ({ access }) => hasAnywhere(access, 'activity.review') },
  { key: 'actividades', label: 'Actividades', to: '/actividades', group: 'operacion', show: () => true },
  { key: 'seguimiento', label: 'Seguimiento', to: '/seguimiento', group: 'operacion', show: () => true },
  {
    key: 'institucional',
    label: 'Tablero institucional',
    to: '/institucional',
    group: 'institucional',
    show: ({ authz }) => can(authz, 'institutional.view') || can(authz, 'unit.read.all'),
  },
  {
    key: 'reportes',
    label: 'Reportes',
    to: '/reportes',
    group: 'institucional',
    show: ({ authz }) => can(authz, 'institutional.view') || can(authz, 'unit.read.all'),
  },
  { key: 'catalogos', label: 'Catálogos', to: '/catalogos', group: 'administracion', show: ({ authz }) => can(authz, 'institutional.view') },
  { key: 'usuarios', label: 'Usuarios', to: '/usuarios', group: 'administracion', show: ({ authz }) => can(authz, 'institutional.view') },
  { key: 'roles', label: 'Roles y permisos', to: '/roles', group: 'administracion', show: ({ access }) => hasAnywhere(access, 'user.manage') },
  { key: 'auditoria', label: 'Auditoría', to: '/bitacora', group: 'administracion', show: ({ authz }) => can(authz, 'audit.read') },
];

/** Los items que esta persona realmente ve, en orden. */
export function visibleNavItems(access: AccessResponse, authz: MyAuthz): NavItem[] {
  return NAV_ITEMS.filter((item) => item.show({ access, authz }));
}

/**
 * Desde cuantos items sueltos conviene agrupar el sidebar (pedido explicito: "5 o mas").
 * Con esto hoy: Administrador (13) y Auditor (6) agrupan; Revisor (4) y Jefe/Colaborador/Consulta
 * (3, sin contar "Mi unidad") se quedan planos como siempre.
 */
export const GROUPING_THRESHOLD = 5;
